'use client'

import Link from 'next/link'

import { BlockHeading } from '@/components/BlockHeading'
import { Tagline } from '@/components/pro-blocks/landing-page/tagline'
import { PayloadRichText } from '@/components/PayloadRichText'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import type { Media, PartnersSectionBlock } from '@/payload-types'
import { cn } from '@/lib/utils'
import { resolveMediaSrc } from '@/utilities/getMediaUrl'
import { hasRichText } from '@/utilities/hasRichText'

type PartnersSectionBlockProps = PartnersSectionBlock

function isMediaDoc(logo: string | Media | null | undefined): logo is Media {
  return Boolean(logo && typeof logo === 'object' && 'url' in logo && logo.url)
}

export function PartnersSectionBlock(props: PartnersSectionBlockProps) {
  const hasTagline = Boolean(props.tagline?.trim())
  const hasHeading = Boolean(props.heading?.trim())
  const hasBody = hasRichText(props.body)
  const headingId = hasHeading ? 'partners-section-heading' : undefined
  const partners = (props.partners ?? []).filter((p) => p.name?.trim() && isMediaDoc(p.logo))

  if (partners.length === 0 && !hasHeading) return null

  return (
    <section aria-labelledby={headingId} className="bg-muted/40">
      <div className="container-padding-x container mx-auto">
        <div className="section-title-gap-md mx-auto flex max-w-2xl flex-col items-center text-center">
          {hasTagline ? <Tagline>{props.tagline}</Tagline> : null}
          {hasHeading ? (
            <BlockHeading
              className="text-foreground"
              headingStyle={props.headingStyle}
              id={headingId}
            >
              {props.heading}
            </BlockHeading>
          ) : null}
          {hasBody ? (
            <PayloadRichText
              className="text-muted-foreground m-0 max-w-none space-y-2 text-base leading-relaxed whitespace-normal [&_a]:underline [&_p]:m-0 [&_p+p]:mt-2"
              data={props.body}
            />
          ) : null}
        </div>

        {partners.length > 0 ? (
          <div className="relative mt-10 px-2 md:px-16">
            <Carousel
              className="w-full"
              opts={{
                align: 'start',
                loop: partners.length > 1,
                slidesToScroll: 1,
              }}
            >
              <CarouselContent className="-ml-3 md:-ml-6">
                {partners.map((partner, index) => {
                  const logo = partner.logo
                  const url = partner.url?.trim()
                  const inner = (
                    <div
                      className={cn(
                        'border-foreground/10 bg-card flex min-h-80 flex-col items-center justify-center border p-2 sm:min-h-96 sm:p-8 md:min-h-120 md:p-4',
                        url && 'transition-opacity hover:opacity-90',
                      )}
                    >
                      {isMediaDoc(logo) ? (
                        <div className="relative h-40 w-80 sm:h-52 sm:w-96 md:h-64 md:max-w-lg md:w-full">
                          <img
                            alt={logo.alt || partner.name || 'Partner logo'}
                            className="absolute inset-0 size-full object-contain"
                            src={resolveMediaSrc(logo)}
                          />
                        </div>
                      ) : null}
                      {partner.name ? (
                        <span className="text-muted-foreground mt-5 max-w-full truncate text-center text-base font-medium sm:text-lg">
                          {partner.name}
                        </span>
                      ) : null}
                    </div>
                  )

                  return (
                    <CarouselItem
                      className="min-w-0 pl-3 sm:basis-[47.5%] md:pl-6 lg:basis-[47.5%]"
                      key={partner.id ?? `${partner.name}-${index}`}
                    >
                      {url ? (
                        <Link
                          className="block outline-none focus-visible:ring-2 focus-visible:ring-foreground/30"
                          href={url}
                          rel="noreferrer"
                          target="_blank"
                        >
                          {inner}
                        </Link>
                      ) : (
                        inner
                      )}
                    </CarouselItem>
                  )
                })}
              </CarouselContent>
              <CarouselPrevious
                className="border-foreground/20 -left-8"
                colorMode="light"
                variant="outline"
              />
              <CarouselNext
                className="border-foreground/20 -right-8"
                colorMode="light"
                variant="outline"
              />
            </Carousel>
          </div>
        ) : null}
      </div>
    </section>
  )
}
